import { createApp  } from 'vue'
import { createPinia } from 'pinia'
import { autoAnimatePlugin } from '@formkit/auto-animate/vue'
import { tracker as openReplayTracker } from '@openreplay/tracker'
import trackerAssist from '@openreplay/tracker-assist'
import VueDatePicker from '@vuepic/vue-datepicker'
import piniaPluginPersistedState from 'pinia-plugin-persistedstate'
// Third-party CSS lives in a `vendor` cascade layer so Tailwind v4 utilities
// keep winning over it (see assets/css/vendor.css).
import './assets/css/vendor.css'
import 'unfonts.css'
import './assets/css/tailwind.css'
import './assets/css/animation.css'
import './assets/css/app.css'
import './assets/css/datePicker.css'
import '@tato30/vue-pdf/style.css'
import VueVirtualScroller from 'vue-virtual-scroller'
import FloatingVue from 'floating-vue'
import App from './App.vue'
import { useApplicationStore } from '@/config/store/applicationStore'
import router from '@/config/router'
import head from '@/config/head'
import { useUserStore } from '@/config/store/userStore'
import { APP_VERSION } from '@/config/constantes'
import i18n from '@/config/translation'
import { vaporInteropPlugin } from 'vue'
import { updateThemeApplication } from '@/services/application'

const ensurePerformanceMethods = () => {
  if (typeof window === 'undefined' || !window.performance)
    return

  const performanceApi = window.performance

  if (typeof performanceApi.clearMarks !== 'function')
    performanceApi.clearMarks = () => {}

  if (typeof performanceApi.clearMeasures !== 'function')
    performanceApi.clearMeasures = () => {}
}

ensurePerformanceMethods()

const application = createApp(App)

const pinia = createPinia()
pinia.use(piniaPluginPersistedState)
application.use(pinia)
application.use(vaporInteropPlugin)


// oxlint-disable-next-line no-new
new Promise(() => {
  if (!window?.location.host.includes('127.0.0.1') && !window?.location.host.includes('localhost')) {
    const isSandbox = window?.location.host.includes('sandbox')


    const userStore = useUserStore()
    if (userStore.user?._id) {
      const userProperties = {
        version: APP_VERSION,
        email: userStore.user.user.email,
        resourceId: userStore.user._id,
        firstname: userStore.user.firstname,
        lastname: userStore.user.lastname,
        profileId: userStore.user.accessProfile,
        profile: userStore.user.profile?.display,
      }
    }
    openReplayTracker.configure({
      projectKey: 'T8tLuS55IWw6rye11yB8',
      ingestPoint: 'https://openreplay.bc.direct/ingest',
      defaultInputMode: 0,
      obscureInputDates: false,
      obscureInputEmails: false,
      obscureTextNumbers: false,
      obscureInputNumbers: false,
      obscureTextEmails: false,
    })
    void openReplayTracker.start()
    openReplayTracker.use(trackerAssist({
      confirmText: 'A developer wants to start a screen sharing session.',
    }))
    if (userStore.user?._id && userStore.user?.user?.email) {
      openReplayTracker.setUserID(userStore.user.user.email)
      openReplayTracker.setMetadata('resourceId', userStore.user._id)
      openReplayTracker.setMetadata('name', `${userStore.user.lastname} ${userStore.user.firstname}`)
      if(userStore.user.accessProfile)openReplayTracker.setMetadata('profileId', userStore.user.accessProfile)
      if(userStore.user.profile)openReplayTracker.setMetadata('profile', userStore.user.profile.display)
      if(userStore.user.firm)openReplayTracker.setMetadata('firm', userStore.user.firm.company)
      if(userStore.user._firmId)openReplayTracker.setMetadata('_firmId', userStore.user._firmId)
      if(userStore.user.currentJob?.positionId)openReplayTracker.setMetadata('jobId', userStore.user.currentJob.positionId)
      if(userStore.user.currentJob?.position?.display)openReplayTracker.setMetadata('job', userStore.user.currentJob.position.display)
      if(userStore.user.picture)openReplayTracker.setMetadata('picture', userStore.user.picture)
    }else {
      openReplayTracker.setMetadata('resourceId', 'not-logged-in')

    }
    openReplayTracker.setMetadata('release', APP_VERSION)
  }
})


const applicationStore = useApplicationStore()
applicationStore.resetErrors()
// Restore firm theme CSS vars from persisted store before first paint of Vue UI
if (applicationStore.theme?.colors?.hsl)
  updateThemeApplication(applicationStore.theme.colors.hsl)
application.use(head)
application.use(router)
application.use(i18n)
application.use(FloatingVue)
application.use(autoAnimatePlugin)
application.use(VueVirtualScroller)
application.component('VueDatePicker', VueDatePicker)

application.mount('#app')

const assetRecoveryNotifier = window.__BC_NOTIFY_APP_MOUNTED__
if (typeof assetRecoveryNotifier === 'function') {
  assetRecoveryNotifier()
}

console.info('[bc-asset-recovery]', 'Application mounted successfully, clearing recovery attempt counter', {
  location: window.location.href,
  previousAttemptCount: window.sessionStorage.getItem('__bc_asset_recovery_attempt_count__'),
})
window.sessionStorage.removeItem('__bc_asset_recovery_attempt_count__')

const currentUrl = new URL(window.location.href)
if (currentUrl.searchParams.has('__bc_asset_reload')) {
  console.info('[bc-asset-recovery]', 'Removing asset reload query parameter after successful mount', {
    location: window.location.href,
  })
  currentUrl.searchParams.delete('__bc_asset_reload')
  window.history.replaceState(window.history.state, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`)
}
